import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Search,
  Loader2,
  Ticket,
  CheckCircle2,
  CalendarDays,
  Clock,
  X,
  CalendarClock,
} from "lucide-react";
import { Calendar } from "../components/ui/calendar";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { api } from "../lib/api";
import { toast } from "sonner";

const formatDate = (d) => {
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};
const isWeekend = (d) => d.getDay() === 0 || d.getDay() === 6;
const isPast = (d) => {
  const t = new Date();
  t.setHours(0, 0, 0, 0);
  return d < t;
};

export default function MyPackPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [packs, setPacks] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [noticeHours, setNoticeHours] = useState(24);

  // booking modal state
  const [bookPack, setBookPack] = useState(null);
  const [date, setDate] = useState(null);
  const [slot, setSlot] = useState("");
  const [availability, setAvailability] = useState({ all_slots: [], available: [] });
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [booking, setBooking] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  // cancel + reschedule state
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [rescheduleTarget, setRescheduleTarget] = useState(null);
  const [reDate, setReDate] = useState(null);
  const [reSlot, setReSlot] = useState("");
  const [reAvail, setReAvail] = useState({ all_slots: [], available: [] });
  const [rescheduling, setRescheduling] = useState(false);

  const refresh = async () => {
    const r = await api.get(`/packs/lookup`, { params: { email } });
    setPacks(r.data.packs);
    setBookings(r.data.bookings);
    if (typeof r.data.cancel_notice_hours === "number") {
      setNoticeHours(r.data.cancel_notice_hours);
    }
  };

  const lookup = async (e) => {
    e?.preventDefault?.();
    if (!email) {
      toast.error("Enter your email");
      return;
    }
    setLoading(true);
    setSearched(false);
    try {
      const { data } = await api.get(`/packs/lookup`, { params: { email } });
      setPacks(data.packs);
      setBookings(data.bookings);
      if (typeof data.cancel_notice_hours === "number") {
        setNoticeHours(data.cancel_notice_hours);
      }
      setSearched(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Lookup failed");
    } finally {
      setLoading(false);
    }
  };

  const openBookModal = async (pack) => {
    setBookPack(pack);
    setDate(null);
    setSlot("");
    setPhone("");
    setNotes("");
    setConfirmed(null);
    setAvailability({ all_slots: [], available: [] });
  };

  const onDateChange = async (d) => {
    setDate(d);
    setSlot("");
    if (!d) return;
    try {
      const { data } = await api.get(`/availability?date=${formatDate(d)}`);
      setAvailability(data);
    } catch (e) {
      console.error(e);
    }
  };

  const confirmBooking = async () => {
    if (!date || !slot) {
      toast.error("Pick a date and time slot");
      return;
    }
    if (!phone) {
      toast.error("Phone number required");
      return;
    }
    setBooking(true);
    try {
      const { data } = await api.post(`/packs/${bookPack.id}/book`, {
        date: formatDate(date),
        time_slot: slot,
        phone,
        notes,
      });
      setConfirmed(data);
      // refresh pack list
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not book session");
    } finally {
      setBooking(false);
    }
  };

  const onReDateChange = async (d) => {
    setReDate(d);
    setReSlot("");
    if (!d) return;
    try {
      const { data } = await api.get(`/availability?date=${formatDate(d)}`);
      setReAvail(data);
    } catch (e) {
      console.error(e);
    }
  };

  const doCancel = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      const { data } = await api.post(`/bookings/${cancelTarget.id}/cancel`, {
        email,
      });
      if (data.refunded_credit) {
        toast.success("Cancelled — pack credit refunded.");
      } else {
        toast.success("Session cancelled.");
      }
      setCancelTarget(null);
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Cancel failed");
    } finally {
      setCancelling(false);
    }
  };

  const doReschedule = async () => {
    if (!rescheduleTarget) return;
    if (!reDate || !reSlot) {
      toast.error("Pick a new date and time slot");
      return;
    }
    setRescheduling(true);
    try {
      await api.post(`/bookings/${rescheduleTarget.id}/reschedule`, {
        email,
        date: formatDate(reDate),
        time_slot: reSlot,
      });
      toast.success("Session rescheduled.");
      setRescheduleTarget(null);
      setReDate(null);
      setReSlot("");
      setReAvail({ all_slots: [], available: [] });
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reschedule failed");
    } finally {
      setRescheduling(false);
    }
  };

  return (
    <div data-testid="customer-portal-page" className="bg-[#050505] text-white min-h-screen">
      <Navbar />
      <main className="pt-28 pb-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/"
            data-testid="mypack-back-link"
            className="inline-flex items-center gap-2 text-xs tracking-widest uppercase text-white/60 hover:text-white"
          >
            <ArrowLeft size={14} /> Back
          </Link>

          <div className="mt-6">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#60A5FA]">
              [ Customer Portal ]
            </div>
            <h1 className="font-display mt-6 text-4xl md:text-5xl lg:text-6xl font-bold uppercase tracking-tight leading-[1.0]">
              Customer <span className="text-[#2563EB]">Portal</span>
            </h1>
            <p className="mt-6 text-[#A1A1AA] max-w-xl">
              Enter your email to see your upcoming sessions, manage bookings,
              and check any active session packs.
            </p>
          </div>

          <form
            onSubmit={lookup}
            data-testid="mypack-lookup-form"
            className="mt-8 bg-[#070B14] border border-white/5 p-6 md:p-8 flex flex-col sm:flex-row gap-3"
          >
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="mypack-email-input"
              placeholder="you@email.com"
              className="bh-input flex-1"
              required
            />
            <button
              type="submit"
              disabled={loading}
              data-testid="mypack-lookup-btn"
              className="inline-flex items-center justify-center gap-2 bg-[#2563EB] hover:bg-[#1D4ED8] disabled:opacity-60 text-white font-bold tracking-widest uppercase text-xs px-6 py-4 rounded-sm transition-colors"
            >
              {loading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
              {loading ? "Looking…" : "Find My Account"}
            </button>
          </form>

          {searched && packs.length === 0 && bookings.length === 0 && (
            <div data-testid="mypack-empty" className="mt-10 bg-[#070B14] border border-white/5 p-10 text-center">
              <Ticket size={32} className="text-[#60A5FA] mx-auto" />
              <h3 className="font-display mt-4 text-xl font-bold uppercase tracking-tight">
                No activity yet
              </h3>
              <p className="mt-3 text-[#A1A1AA] text-sm">
                We couldn't find any sessions or session packs for that email.
                Book a session or grab a pack to get started.
              </p>
              <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
                <Link
                  to="/book"
                  data-testid="mypack-empty-book-cta"
                  className="inline-flex items-center justify-center gap-2 bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-bold tracking-widest uppercase text-xs px-6 py-3 rounded-sm"
                >
                  Book a Session <ArrowRight size={14} />
                </Link>
                <Link
                  to="/#memberships"
                  data-testid="mypack-empty-cta"
                  className="inline-flex items-center justify-center gap-2 border border-white/15 hover:border-[#2563EB] hover:text-[#60A5FA] text-white font-bold tracking-widest uppercase text-xs px-6 py-3 rounded-sm"
                >
                  View Packs
                </Link>
              </div>
            </div>
          )}

          <div className="flex flex-col">
          {packs.length > 0 && (
            <div className="mt-10 space-y-4 order-2">
              <div className="text-[10px] tracking-[0.3em] uppercase text-white/50">
                Active Packs
              </div>
              {packs.map((p) => {
                const used = p.sessions_used;
                const total = p.sessions_total;
                const remaining = p.sessions_remaining;
                const pct = total > 0 ? (used / total) * 100 : 0;
                return (
                  <div
                    key={p.id}
                    data-testid={`mypack-card-${p.id}`}
                    className="bg-[#070B14] border border-white/5 p-6 md:p-8"
                  >
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                      <div>
                        <div className="text-[10px] tracking-[0.3em] uppercase text-[#60A5FA]">
                          {p.pack_id?.includes("1on1") ? "1-on-1" : p.pack_id?.includes("small") ? "Small Group" : "Large Group"} · 4-Pack
                        </div>
                        <h3 className="font-display mt-2 text-2xl md:text-3xl font-bold uppercase tracking-tight">
                          {p.program}
                        </h3>
                        <div className="mt-3 text-[#A1A1AA] text-sm">
                          Athlete: {p.name || "—"}
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <div className="font-display text-4xl md:text-5xl font-bold text-[#60A5FA]">
                            {remaining}
                          </div>
                          <div className="text-[10px] tracking-[0.3em] uppercase text-white/50 mt-1">
                            of {total} left
                          </div>
                        </div>
                        <button
                          disabled={remaining === 0}
                          onClick={() => openBookModal(p)}
                          data-testid={`mypack-book-${p.id}`}
                          className="inline-flex items-center gap-2 bg-[#2563EB] hover:bg-[#1D4ED8] disabled:bg-white/10 disabled:text-white/40 text-white font-bold tracking-widest uppercase text-xs px-5 py-3 rounded-sm transition-colors"
                        >
                          {remaining === 0 ? "Pack Used" : (
                            <>
                              Book Session <ArrowRight size={14} />
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                    {/* Progress bar */}
                    <div className="mt-6 h-1 w-full bg-white/5">
                      <div
                        className="h-full bg-[#2563EB] transition-all"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <div className="mt-2 flex justify-between text-[10px] tracking-widest uppercase text-white/40">
                      <span>{used} used</span>
                      <span>{total} total</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {bookings.length > 0 && (
            <div className="mt-10 order-1">
              <div className="flex items-end justify-between mb-4">
                <div className="text-[10px] tracking-[0.3em] uppercase text-white/50">
                  Your Upcoming Sessions
                </div>
                <div className="text-[10px] tracking-widest uppercase text-white/40">
                  Cancellations require {noticeHours}h notice
                </div>
              </div>
              <div className="bg-[#070B14] border border-white/5 divide-y divide-white/5">
                {bookings.map((b) => (
                  <div
                    key={b.id}
                    data-testid={`mypack-booking-${b.id}`}
                    className="px-6 py-4 flex flex-col md:flex-row md:items-center justify-between gap-3"
                  >
                    <div className="flex items-center gap-4">
                      <CalendarDays size={16} className="text-[#60A5FA]" />
                      <div>
                        <div className="text-white font-medium">{b.date}</div>
                        <div className="text-[10px] tracking-widest uppercase text-white/40">
                          {b.program}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="text-[#60A5FA] font-display font-semibold">
                        {b.time_slot}
                      </span>
                      <span
                        className={`text-[10px] tracking-[0.25em] uppercase px-2 py-1 border ${
                          b.status === "confirmed"
                            ? "border-[#2563EB] text-[#60A5FA]"
                            : "border-white/15 text-white/50"
                        }`}
                      >
                        {b.status.replace("_", " ")}
                      </span>
                      {b.status === "confirmed" && (
                        <>
                          <button
                            onClick={() => {
                              setRescheduleTarget(b);
                              setReDate(null);
                              setReSlot("");
                            }}
                            data-testid={`mypack-reschedule-${b.id}`}
                            className="inline-flex items-center gap-1 text-[10px] tracking-[0.25em] uppercase border border-white/15 hover:border-[#2563EB] hover:text-[#60A5FA] px-3 py-1.5 transition-colors"
                          >
                            <CalendarClock size={12} /> Reschedule
                          </button>
                          {b.can_cancel ? (
                            <button
                              onClick={() => setCancelTarget(b)}
                              data-testid={`mypack-cancel-${b.id}`}
                              className="inline-flex items-center gap-1 text-[10px] tracking-[0.25em] uppercase border border-white/15 hover:border-red-500/60 hover:text-red-400 px-3 py-1.5 transition-colors"
                            >
                              <X size={12} /> Cancel
                            </button>
                          ) : (
                            <span
                              data-testid={`mypack-cancel-locked-${b.id}`}
                              title={`Cancellations require ${noticeHours}h notice. Please reschedule.`}
                              className="inline-flex items-center gap-1 text-[10px] tracking-[0.25em] uppercase border border-white/5 text-white/30 px-3 py-1.5 cursor-not-allowed"
                            >
                              <X size={12} /> {noticeHours}h Notice
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          </div>
        </div>
      </main>

      {/* Book-from-pack modal */}
      {bookPack && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center px-4 py-8 overflow-y-auto"
          onClick={() => !booking && setBookPack(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            data-testid="mypack-book-modal"
            className="bg-[#070B14] border border-white/10 max-w-2xl w-full p-8 my-auto"
          >
            {confirmed ? (
              <div className="text-center py-6">
                <CheckCircle2 size={48} className="text-[#2563EB] mx-auto" />
                <h3 className="font-display mt-4 text-2xl font-bold uppercase tracking-tight">
                  Session Booked
                </h3>
                <p className="mt-3 text-[#A1A1AA]">
                  {confirmed.booking.date} · {confirmed.booking.time_slot}
                </p>
                <p className="mt-2 text-sm text-[#60A5FA]">
                  {confirmed.sessions_remaining} sessions remaining
                </p>
                <button
                  onClick={() => setBookPack(null)}
                  data-testid="mypack-confirmed-close"
                  className="mt-8 bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-bold tracking-widest uppercase text-xs px-6 py-3 rounded-sm"
                >
                  Done
                </button>
              </div>
            ) : (
              <>
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#60A5FA]">
                  [ Redeem 1 Session ]
                </div>
                <h3 className="font-display mt-2 text-2xl font-bold uppercase tracking-tight">
                  {bookPack.program}
                </h3>
                <div className="mt-1 text-sm text-[#A1A1AA]">
                  {bookPack.sessions_remaining} of {bookPack.sessions_total} sessions remaining
                </div>

                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <div className="text-[10px] tracking-[0.3em] uppercase text-white/60 mb-3">
                      Select Date
                    </div>
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={onDateChange}
                      disabled={(d) => isPast(d) || isWeekend(d)}
                      className="text-white"
                    />
                  </div>
                  <div>
                    <div className="text-[10px] tracking-[0.3em] uppercase text-white/60 mb-3">
                      Time Slot
                    </div>
                    {!date ? (
                      <div className="text-white/40 text-sm">Pick a date first</div>
                    ) : (
                      <div className="grid grid-cols-2 gap-2">
                        {availability.all_slots.map((s) => {
                          const isAvail = availability.available.includes(s);
                          const isSelected = slot === s;
                          return (
                            <button
                              key={s}
                              disabled={!isAvail}
                              onClick={() => setSlot(s)}
                              data-testid={`mypack-slot-${s.replace(/[: ]/g, "")}`}
                              className={`px-3 py-2 text-xs font-bold tracking-wider uppercase border transition-colors ${
                                isSelected
                                  ? "bg-[#2563EB] border-[#2563EB] text-white"
                                  : isAvail
                                  ? "border-white/15 text-white hover:border-[#2563EB]"
                                  : "border-white/5 text-white/30 line-through cursor-not-allowed"
                              }`}
                            >
                              <Clock size={10} className="inline mr-1 -mt-0.5" />
                              {s}
                            </button>
                          );
                        })}
                      </div>
                    )}

                    <div className="mt-6 space-y-4">
                      <label className="block">
                        <span className="text-[10px] tracking-[0.3em] uppercase text-white/60">
                          Phone
                        </span>
                        <input
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                          data-testid="mypack-phone-input"
                          className="bh-input mt-2"
                          placeholder="(555) 555-5555"
                        />
                      </label>
                      <label className="block">
                        <span className="text-[10px] tracking-[0.3em] uppercase text-white/60">
                          Notes
                        </span>
                        <textarea
                          value={notes}
                          onChange={(e) => setNotes(e.target.value)}
                          data-testid="mypack-notes-input"
                          rows={2}
                          className="bh-input mt-2"
                          placeholder="Anything to focus on?"
                        />
                      </label>
                    </div>
                  </div>
                </div>

                <div className="mt-8 flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={() => setBookPack(null)}
                    disabled={booking}
                    data-testid="mypack-modal-cancel"
                    className="flex-1 border border-white/15 hover:border-white/40 text-white font-bold tracking-widest uppercase text-xs px-6 py-3 rounded-sm"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmBooking}
                    disabled={booking}
                    data-testid="mypack-confirm-btn"
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-[#2563EB] hover:bg-[#1D4ED8] disabled:opacity-60 text-white font-bold tracking-widest uppercase text-xs px-6 py-3 rounded-sm"
                  >
                    {booking ? <Loader2 size={14} className="animate-spin" /> : null}
                    Confirm · Use 1 Session
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Cancel confirmation modal */}
      {cancelTarget && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center px-4"
          onClick={() => !cancelling && setCancelTarget(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            data-testid="mypack-cancel-modal"
            className="bg-[#070B14] border border-white/10 max-w-md w-full p-8"
          >
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#60A5FA]">
              [ Cancel Session ]
            </div>
            <h3 className="font-display mt-3 text-2xl font-bold uppercase tracking-tight">
              Cancel this session?
            </h3>
            <div className="mt-4 bg-[#050505] border border-white/5 p-4 text-sm">
              <div className="text-white">{cancelTarget.date} · {cancelTarget.time_slot}</div>
              <div className="text-[10px] tracking-widest uppercase text-white/40 mt-1">
                {cancelTarget.program}
              </div>
            </div>
            <p className="mt-4 text-[#A1A1AA] text-sm">
              {cancelTarget.pack_record_id
                ? "Your pack credit will be refunded automatically."
                : "This session was paid by card. Contact the coach if you need a refund."}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => setCancelTarget(null)}
                disabled={cancelling}
                data-testid="mypack-cancel-modal-back"
                className="flex-1 border border-white/15 hover:border-white/40 text-white font-bold tracking-widest uppercase text-xs px-6 py-3 rounded-sm"
              >
                Keep Session
              </button>
              <button
                onClick={doCancel}
                disabled={cancelling}
                data-testid="mypack-cancel-confirm-btn"
                className="flex-1 inline-flex items-center justify-center gap-2 bg-red-500/90 hover:bg-red-500 disabled:opacity-60 text-white font-bold tracking-widest uppercase text-xs px-6 py-3 rounded-sm"
              >
                {cancelling ? <Loader2 size={14} className="animate-spin" /> : <X size={14} />}
                Confirm Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reschedule modal */}
      {rescheduleTarget && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center px-4 py-8 overflow-y-auto"
          onClick={() => !rescheduling && setRescheduleTarget(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            data-testid="mypack-reschedule-modal"
            className="bg-[#070B14] border border-white/10 max-w-2xl w-full p-8 my-auto"
          >
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#60A5FA]">
              [ Reschedule Session ]
            </div>
            <h3 className="font-display mt-2 text-2xl font-bold uppercase tracking-tight">
              {rescheduleTarget.program}
            </h3>
            <div className="mt-3 bg-[#050505] border border-white/5 p-3 text-sm flex items-center justify-between">
              <span className="text-[10px] tracking-widest uppercase text-white/50">Currently</span>
              <span className="text-white">
                {rescheduleTarget.date} · {rescheduleTarget.time_slot}
              </span>
            </div>

            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-white/60 mb-3">
                  New Date
                </div>
                <Calendar
                  mode="single"
                  selected={reDate}
                  onSelect={onReDateChange}
                  disabled={(d) => isPast(d) || isWeekend(d)}
                  className="text-white"
                />
              </div>
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-white/60 mb-3">
                  New Time
                </div>
                {!reDate ? (
                  <div className="text-white/40 text-sm">Pick a date first</div>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    {reAvail.all_slots.map((s) => {
                      const isAvail = reAvail.available.includes(s);
                      const isSelected = reSlot === s;
                      return (
                        <button
                          key={s}
                          disabled={!isAvail}
                          onClick={() => setReSlot(s)}
                          data-testid={`mypack-re-slot-${s.replace(/[: ]/g, "")}`}
                          className={`px-3 py-2 text-xs font-bold tracking-wider uppercase border transition-colors ${
                            isSelected
                              ? "bg-[#2563EB] border-[#2563EB] text-white"
                              : isAvail
                              ? "border-white/15 text-white hover:border-[#2563EB]"
                              : "border-white/5 text-white/30 line-through cursor-not-allowed"
                          }`}
                        >
                          <Clock size={10} className="inline mr-1 -mt-0.5" />
                          {s}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => setRescheduleTarget(null)}
                disabled={rescheduling}
                data-testid="mypack-reschedule-cancel"
                className="flex-1 border border-white/15 hover:border-white/40 text-white font-bold tracking-widest uppercase text-xs px-6 py-3 rounded-sm"
              >
                Keep Original
              </button>
              <button
                onClick={doReschedule}
                disabled={rescheduling}
                data-testid="mypack-reschedule-confirm-btn"
                className="flex-1 inline-flex items-center justify-center gap-2 bg-[#2563EB] hover:bg-[#1D4ED8] disabled:opacity-60 text-white font-bold tracking-widest uppercase text-xs px-6 py-3 rounded-sm"
              >
                {rescheduling ? <Loader2 size={14} className="animate-spin" /> : <CalendarClock size={14} />}
                Confirm New Time
              </button>
            </div>
          </div>
        </div>
      )}


      <Footer />

      <style>{`
        .bh-input { width:100%; background:#050505; border:1px solid rgba(255,255,255,0.08); color:#fff; padding:14px 16px; font-size:14px; outline:none; border-radius:2px; font-family:inherit; }
        .bh-input:focus { border-color:#2563EB; }
        .bh-input::placeholder { color:rgba(255,255,255,0.3); }
      `}</style>
    </div>
  );
}
